import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Map, { Marker } from 'react-map-gl';
import api_endpoint from '../../../settings';
import { useTranslation } from 'react-i18next';
import './AddTrackModal.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoidG9yZ3lkIiwiYSI6ImNtNXN6ZGQ0bjBhbGIya3M5dHpwY2p3aDkifQ.placeholder';

function AddTrackModal({ tourId, track, onClose, onSuccess }) {
    const { t } = useTranslation();
    const accessToken = useSelector(state => state.Auth.accessToken);

    const [audioFile, setAudioFile] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [textContent, setTextContent] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [sequenceOrder, setSequenceOrder] = useState(0);
    const [duration, setDuration] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (track) {
            setTitle(track.title || '');
            setDescription(track.description || '');
            setTextContent(track.text_content || '');
            setLatitude(track.latitude != null ? String(track.latitude) : '');
            setLongitude(track.longitude != null ? String(track.longitude) : '');
            setSequenceOrder(track.sequence_order ?? 0);
            setDuration(track.duration_seconds ?? null);
        }
    }, [track]);

    const initialCenter = useMemo(() => {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng, zoom: 15 };
        return { lat: 37.7749, lng: -122.4194, zoom: 11 }; // fallback
    }, [latitude, longitude]);

    const [mapViewState, setMapViewState] = useState({
        latitude: 37.7749,
        longitude: -122.4194,
        zoom: 11,
    });

    useEffect(() => {
        setMapViewState({
            latitude: initialCenter.lat,
            longitude: initialCenter.lng,
            zoom: initialCenter.zoom,
        });
    }, [initialCenter.lat, initialCenter.lng, initialCenter.zoom]);

    const setLocation = (lat, lng) => {
        setLatitude(String(lat));
        setLongitude(String(lng));
    };

    // Handle audio file selection and extract duration
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('audio/')) {
            setErrors({ audioFile: t('track-error-select-audio') });
            return;
        }

        setAudioFile(file);
        setErrors({ ...errors, audioFile: null });

        // Extract duration using Web Audio API
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const arrayBuffer = await file.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            const durationSeconds = Math.floor(audioBuffer.duration);
            setDuration(durationSeconds);
            audioContext.close();
        } catch (error) {
            console.error('Error extracting audio duration:', error);
            setErrors({ ...errors, audioFile: t('track-error-audio-duration') });
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!title.trim()) newErrors.title = t('track-error-title-required');
        if (!latitude || isNaN(latitude) || latitude < -90 || latitude > 90) {
            newErrors.latitude = t('track-error-valid-latitude');
        }
        if (!longitude || isNaN(longitude) || longitude < -180 || longitude > 180) {
            newErrors.longitude = t('track-error-valid-longitude');
        }
        if (!track && !audioFile && !textContent.trim()) {
            newErrors.audioFile = t('track-error-audio-or-text');
            newErrors.textContent = t('track-error-text-or-audio');
        }
        if (sequenceOrder < 0) newErrors.sequenceOrder = t('track-error-sequence');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const uploadAudioFile = async () => {
        if (!audioFile) return null;

        const presignedResponse = await fetch(
            `${api_endpoint}/tourguide/tours/${tourId}/tracks/presigned-url`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    filename: audioFile.name,
                    content_type: audioFile.type || 'application/octet-stream',
                }),
            }
        );
        if (!presignedResponse.ok) {
            throw new Error(t('track-error-get-upload-url'));
        }

        const presignedData = await presignedResponse.json();
        const uploadResponse = await fetch(presignedData.presigned_url, {
            method: 'PUT',
            body: audioFile,
            headers: {
                'Content-Type': audioFile.type || 'application/octet-stream',
            },
        });
        if (!uploadResponse.ok) {
            throw new Error(t('track-error-upload-audio'));
        }

        return presignedData.audio_url;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setUploading(true);
        setErrors({});

        try {
            const updateData = {
                title: title.trim(),
                description: description.trim(),
                text_content: textContent.trim() || null,
                latitude: parseFloat(latitude),
                longitude: parseFloat(longitude),
                sequence_order: parseInt(sequenceOrder),
            };

            if (track) {
                const response = await fetch(`${api_endpoint}/tourguide/tours/${tourId}/tracks/${track.id}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updateData),
                });
                if (!response.ok) throw new Error(t('track-error-update-stop'));
            } else {
                const audioUrl = await uploadAudioFile();
                const trackData = {
                    ...updateData,
                    duration_seconds: audioFile ? duration : null,
                    filename: audioFile ? audioFile.name : null,
                    audio_url: audioUrl,
                };

                const response = await fetch(`${api_endpoint}/tourguide/tours/${tourId}/tracks`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(trackData),
                });
                if (!response.ok) throw new Error(t('track-error-create-stop'));
            }
            setUploading(false);
            onSuccess();
        } catch (error) {
            setUploading(false);
            setErrors({ form: error.message || t('track-error-save-stop') });
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{track ? t('track-edit-stop') : t('track-add-stop')}</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="track-form">
                    {!track && (
                        <div className="form-section">
                            <label className="form-label">{t('track-audio-file')}</label>
                            <input
                                type="file"
                                accept="audio/*"
                                onChange={handleFileChange}
                                className={`form-input ${errors.audioFile ? 'error' : ''}`}
                            />
                            {errors.audioFile && <span className="error-text">{errors.audioFile}</span>}
                            {audioFile && duration && (
                                <p className="file-info">
                                    {audioFile.name} ({Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')})
                                </p>
                            )}
                        </div>
                    )}

                    <div className="form-section">
                        <label className="form-label">{t('track-stop-title')}</label>
                        <input
                            type="text"
                            className={`form-input ${errors.title ? 'error' : ''}`}
                            placeholder={t('track-stop-title-placeholder')}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        {errors.title && <span className="error-text">{errors.title}</span>}
                    </div>

                    <div className="form-section">
                        <label className="form-label">{t('track-description')}</label>
                        <textarea
                            className="form-input textarea"
                            placeholder={t('track-description-placeholder')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="3"
                        />
                    </div>

                    <div className="form-section">
                        <label className="form-label">{t('track-text-content')}</label>
                        <textarea
                            className={`form-input textarea ${errors.textContent ? 'error' : ''}`}
                            placeholder={t('track-text-content-placeholder')}
                            value={textContent}
                            onChange={(e) => setTextContent(e.target.value)}
                            rows="4"
                        />
                        {errors.textContent && <span className="error-text">{errors.textContent}</span>}
                    </div>

                    <div className="form-section">
                        <label className="form-label">{t('track-stop-location')}</label>
                        <div className="help-text">
                            {t('track-location-help')}
                        </div>

                        <div className="stop-location-map">
                            <Map
                                {...mapViewState}
                                onMove={(evt) => setMapViewState(evt.viewState)}
                                onClick={(evt) => setLocation(evt.lngLat.lat, evt.lngLat.lng)}
                                mapStyle="mapbox://styles/mapbox/streets-v12"
                                mapboxAccessToken={MAPBOX_TOKEN}
                                style={{ width: '100%', height: '100%' }}
                            >
                                {(() => {
                                    const lat = parseFloat(latitude);
                                    const lng = parseFloat(longitude);
                                    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
                                    return (
                                        <Marker
                                            latitude={lat}
                                            longitude={lng}
                                            anchor="bottom"
                                            draggable
                                            onDragEnd={(e) => setLocation(e.lngLat.lat, e.lngLat.lng)}
                                        >
                                            <div className="stop-pin">📍</div>
                                        </Marker>
                                    );
                                })()}
                            </Map>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-section half">
                            <label className="form-label">{t('track-latitude')}</label>
                            <input
                                type="number"
                                step="any"
                                className={`form-input ${errors.latitude ? 'error' : ''}`}
                                placeholder="37.7749"
                                value={latitude}
                                onChange={(e) => setLatitude(e.target.value)}
                            />
                            {errors.latitude && <span className="error-text">{errors.latitude}</span>}
                        </div>

                        <div className="form-section half">
                            <label className="form-label">{t('track-longitude')}</label>
                            <input
                                type="number"
                                step="any"
                                className={`form-input ${errors.longitude ? 'error' : ''}`}
                                placeholder="-122.4194"
                                value={longitude}
                                onChange={(e) => setLongitude(e.target.value)}
                            />
                            {errors.longitude && <span className="error-text">{errors.longitude}</span>}
                        </div>
                    </div>

                    <div className="form-section">
                        <label className="form-label">{t('track-sequence-order')}</label>
                        <input
                            type="number"
                            className={`form-input ${errors.sequenceOrder ? 'error' : ''}`}
                            placeholder="0"
                            value={sequenceOrder}
                            onChange={(e) => setSequenceOrder(e.target.value)}
                            min="0"
                        />
                        {errors.sequenceOrder && <span className="error-text">{errors.sequenceOrder}</span>}
                        <p className="help-text">{t('track-sequence-help-zero')}</p>
                    </div>

                    {errors.form && <div className="error-message">{errors.form}</div>}

                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={uploading}>
                            {t('cancel')}
                        </button>
                        <button type="submit" className="btn-primary" disabled={uploading}>
                            {uploading ? t('track-uploading') : track ? t('track-update-stop') : t('track-add-stop')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddTrackModal;
